import React, { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CalendarIcon, MapIcon, LoaderIcon } from 'lucide-react'

import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Alert, AlertDescription } from './ui/alert'
import { useAppStore } from '../stores/useAppStore'
import { workOrderService } from '../services/workOrderService'
import { runsheetService, type Technician } from '../services/runsheetService'

interface RunningOrderModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultTechnicianId?: number
  defaultDate?: string
}

interface OptimizeRequest {
  technicianId: number
  date: string
}

interface ScheduledStop {
  order: number
  type: string
  address: string
  scheduledTime: string
  durationMinutes: number
  workOrderId?: number
  keyNumber?: string
  description?: string
  timeWindow: string
}

interface OptimizedSchedule {
  date: string
  technicianId: number
  stops: ScheduledStop[]
  totalDrivingMinutes: number
  totalWorkMinutes: number
  startTime: string
  endTime: string
}

export const RunningOrderModal: React.FC<RunningOrderModalProps> = ({
  open,
  onOpenChange,
  defaultTechnicianId,
  defaultDate
}) => {
  const { user, addNotification } = useAppStore()
  const [technicianId, setTechnicianId] = useState(defaultTechnicianId || user?.id || '')
  const [date, setDate] = useState(defaultDate || new Date().toISOString().split('T')[0])

  // Fetch technicians list
  const { data: technicians = [], isLoading: techniciansLoading } = useQuery({
    queryKey: ['technicians'],
    queryFn: () => runsheetService.getTechnicians(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const optimizeMutation = useMutation<OptimizedSchedule, Error, OptimizeRequest>({
    mutationFn: async ({ technicianId, date }) => {
      return await workOrderService.optimizeRunningOrder(technicianId, date)
    },
    onSuccess: (data) => {
      const jobCount = data.stops.filter(stop => stop.type === 'Job').length
      const totalHours = Math.round((data.totalDrivingMinutes + data.totalWorkMinutes) / 60 * 10) / 10
      
      addNotification({
        type: 'success',
        message: `Running order optimized successfully! ${jobCount} jobs scheduled over ${totalHours} hours.`,
        timestamp: Date.now()
      })
      
      onOpenChange(false)
      
      // Optionally, you could trigger a refetch of work orders here
      // This would require passing a refetch function as a prop
    },
    onError: (error) => {
      console.error('Optimization error:', error)
      addNotification({
        type: 'error',
        message: `Failed to optimize running order: ${error.message}`,
        timestamp: Date.now()
      })
    }
  })

  const handleOptimize = () => {
    if (!technicianId || !date) {
      addNotification({
        type: 'error',
        message: 'Please provide both technician ID and date.',
        timestamp: Date.now()
      })
      return
    }

    optimizeMutation.mutate({
      technicianId: Number(technicianId),
      date
    })
  }

  const handleClose = () => {
    if (!optimizeMutation.isPending) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Execute Running Order
          </DialogTitle>
          <DialogDescription>
            Optimize the work order sequence for the shortest travel path. This will update the order and scheduled times for all work orders.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="technicianId">Technician</Label>
            <select
              id="technicianId"
              value={technicianId}
              onChange={(e) => setTechnicianId(e.target.value)}
              disabled={optimizeMutation.isPending || techniciansLoading}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Select a technician...</option>
              {technicians.map((tech: Technician) => (
                <option key={tech.id} value={tech.id}>
                  {tech.name} ({tech.username})
                </option>
              ))}
            </select>
            {techniciansLoading && (
              <p className="text-sm text-muted-foreground">Loading technicians...</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="date">Date</Label>
            <div className="relative">
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={optimizeMutation.isPending}
                className="pl-10"
              />
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {optimizeMutation.error && (
            <Alert variant="destructive">
              <AlertDescription>
                {optimizeMutation.error.message}
              </AlertDescription>
            </Alert>
          )}

          {optimizeMutation.isPending && (
            <Alert>
              <LoaderIcon className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Optimizing running order... This may take a few moments.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={optimizeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleOptimize}
            disabled={optimizeMutation.isPending || !technicianId || !date}
          >
            {optimizeMutation.isPending ? (
              <>
                <LoaderIcon className="h-4 w-4 animate-spin mr-2" />
                Optimizing...
              </>
            ) : (
              <>
                <MapIcon className="h-4 w-4 mr-2" />
                Optimize Order
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}